/**
 * One catalogue outcome as the Add Outcomes modal offers it — `.m-outcome` in
 * the prototype's modals.css (CAE-1553).
 *
 * Shares its five-column grid with the market card's head, so the head stays
 * over its columns, and with `fixture-market-drilldown.tsx`, which draws the
 * read-only form of the same row. That one is the catalogue's; this one is the
 * picker's, and the difference is the checkbox.
 */
import Box from '@mui/material/Box';
import type { Theme } from '@mui/material/styles';
import type { CatalogueOutcome } from '../../../../api/catalogue';
import { FixtureSelectCheckbox, OutcomeStatus } from '../../../../components/ui';
import { outcomePickLabel } from './add-outcomes-picks';

/** `.m-outcomes-head` / `.m-outcome` share one grid. */
export const OUTCOME_PICK_COLUMNS = '1fr 90px 90px 110px';

const Price = ({ children }: { children: string | null }) => (
  <Box
    sx={(theme: Theme) => ({
      fontFamily: theme.tokens.font.mono,
      fontSize: '13px',
      fontWeight: 500,
      color: children === null ? theme.tokens.color.ink4 : theme.tokens.color.ink2,
      textAlign: 'right',
      fontVariantNumeric: 'tabular-nums',
      letterSpacing: '-0.01em',
    })}
  >
    {children ?? '—'}
  </Box>
);

export interface OutcomePickRowProps {
  outcome: CatalogueOutcome;
  selected: boolean;
  onSelectedChange: (selected: boolean) => void;
  /**
   * Already a Reel Option on this reel. The row draws ticked and inert with a
   * tag saying so — an operator looking for an outcome needs to see that it is
   * already there, which a hidden row cannot tell them.
   */
  onReel: boolean;
}

export const OutcomePickRow = ({
  outcome,
  selected,
  onSelectedChange,
  onReel,
}: OutcomePickRowProps) => {
  const label = outcomePickLabel(outcome);

  return (
    <Box
      sx={(theme: Theme) => ({
        display: 'grid',
        gridTemplateColumns: OUTCOME_PICK_COLUMNS,
        gap: '12px',
        alignItems: 'center',
        padding: '9px 14px',
        borderTop: theme.tokens.border.default,
        ...(onReel ? { opacity: 0.55 } : {}),
        ...(selected && !onReel
          ? {
              backgroundColor: theme.tokens.color.accentSoft,
              boxShadow: `inset 0 0 0 ${theme.tokens.border.width} ${theme.tokens.componentColor.outcomeSelectedInset}`,
            }
          : { '&:hover': { backgroundColor: theme.tokens.color.surfaceMuted } }),
      })}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', minWidth: 0 }}>
        <FixtureSelectCheckbox
          checked={selected || onReel}
          onChange={onSelectedChange}
          label={`Select ${label}`}
          disabled={onReel}
        />
        <Box
          component="span"
          sx={(theme: Theme) => ({
            fontSize: '13.5px',
            fontWeight: selected && !onReel ? 600 : 500,
            letterSpacing: '-0.005em',
            color: selected && !onReel ? theme.tokens.color.accentStrong : theme.tokens.color.ink,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          })}
        >
          {label}
        </Box>
        {onReel ? (
          <Box
            component="span"
            sx={(theme: Theme) => ({
              flexShrink: 0,
              fontSize: '11px',
              fontWeight: 500,
              color: theme.tokens.color.ink4,
              backgroundColor: theme.tokens.color.bg,
              padding: '2px 7px',
              borderRadius: `${theme.tokens.radius.badge}px`,
            })}
          >
            On reel
          </Box>
        ) : null}
      </Box>

      <Price>{outcome.decimalPrice === null ? null : outcome.decimalPrice.toFixed(2)}</Price>
      <Price>{outcome.fractionalPrice}</Price>
      <Box sx={{ fontSize: '12px' }}>
        <OutcomeStatus suspended={outcome.active === null ? null : !outcome.active} />
      </Box>
    </Box>
  );
};
